import { getApiBaseUrl } from '@/lib/api-config'
import { apiErrorDetailText } from '@/lib/api-error-body'
import {
  getBackendAuthToken,
  type GetAuthToken,
} from '@/lib/backend-auth-token'
import {
  createTraceHeaders,
  mergeTraceIntoDetails,
  readTraceHeaders,
  type TraceContext,
} from '@/lib/trace-context'

export interface ApiError {
  message: string
  code?: string
  details?: unknown
}

export class ApiFetchError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly url: string,
    public readonly code?: string,
    public readonly details?: unknown,
    public readonly traceId?: string,
    public readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiFetchError'
  }
}

/** Upper bound on one SWR read, including its body. */
export const SWR_READ_TIMEOUT_MS = 60_000

export function isApiNotFoundError(error: unknown): error is ApiFetchError {
  return error instanceof ApiFetchError && error.statusCode === 404
}

export interface ApiResponse<T> {
  success: boolean
  data?: T | null
  error?: ApiError | null
}

export interface FetcherOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /**
   * Query params. Array values serialize as repeated params
   * (`?type=a&type=b`) so FastAPI `list[str] = Query()` endpoints receive
   * every value instead of a single comma-joined string.
   */
  query?: Record<string, string | string[]>
  headers?: Record<string, string>
  /**
   * Aborts the request. GETs always get the read deadline; a POST that only
   * reads (e.g. a report query) passes its own signal to be cancellable too.
   */
  signal?: AbortSignal
}

/**
 * Serialize a query record to a `a=b&c=d` string. Array values become repeated
 * params (`type=a&type=b`) so FastAPI `list[str] = Query()` endpoints receive
 * every value; empty arrays contribute nothing. Returns "" for an empty query.
 *
 * Shared by `buildUrl` here and `useApiQuery`'s cache-key builder so the
 * encode/decode round-trip is defined in exactly one place.
 */
export function buildQueryString(
  query?: Record<string, string | string[]>,
): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, item)
    } else {
      params.append(key, value)
    }
  }
  return params.toString()
}

/**
 * Inverse of {@link buildQueryString}: parse a `a=b&type=x&type=y` string back
 * into a record, grouping repeated params into arrays so multi-value facets are
 * not collapsed to their last value. Single-valued params stay strings.
 */
export function parseQueryString(
  queryString: string,
): Record<string, string | string[]> {
  const grouped: Record<string, string | string[]> = {}
  for (const [key, value] of new URLSearchParams(queryString)) {
    const existing = grouped[key]
    if (existing === undefined) {
      grouped[key] = value
    } else if (Array.isArray(existing)) {
      existing.push(value)
    } else {
      grouped[key] = [existing, value]
    }
  }
  return grouped
}

/**
 * Build a full URL with base URL and query parameters.
 * Prepends the backend API base URL for /api/ paths. Array values are appended
 * as repeated params so multi-select facets reach `list[str]` backends intact.
 */
function buildUrl(
  url: string,
  query?: Record<string, string | string[]>,
): string {
  // Prepend base URL for API paths
  const baseUrl = getApiBaseUrl()
  const fullUrl = url.startsWith('/api/') && baseUrl ? `${baseUrl}${url}` : url

  const queryString = buildQueryString(query)
  return queryString ? `${fullUrl}?${queryString}` : fullUrl
}

/**
 * Core fetch function that handles auth token and JSON serialization.
 * Returns the parsed API response.
 */
async function apiFetcher<T>(
  url: string,
  options: FetcherOptions,
  getToken?: GetAuthToken,
): Promise<ApiResponse<T>> {
  const { method = 'GET', body, query, headers = {}, signal } = options

  const finalUrl = buildUrl(url, query)
  const token = await getBackendAuthToken(getToken)
  const trace = createTraceHeaders()

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...trace.headers,
    ...headers,
  }

  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`
  }

  const requestInit: RequestInit = {
    method,
    headers: requestHeaders,
  }

  if (body && method !== 'GET') {
    requestInit.body = JSON.stringify(body)
  }

  const response = await fetch(finalUrl, requestInit)
  let data: ApiResponse<T>

  // Handle empty responses (e.g. 204 No Content) before parsing JSON
  if (
    response.status === 204 ||
    (response.ok && response.headers.get('content-length') === '0')
  ) {
    return { success: true, data: null } as ApiResponse<T>
  }

  try {
    data = (await response.json()) as ApiResponse<T>
  } catch (parseError) {
    // If JSON parsing fails, still check response status
    if (!response.ok) {
      const responseTrace = readTraceHeaders(response)
      throw new ApiFetchError(
        `Failed to parse response: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`,
        response.status,
        finalUrl,
        undefined,
        undefined,
        responseTrace.traceId,
        responseTrace.requestId,
      )
    }
    throw parseError
  }
  data = attachTraceToApiResponse(data, readTraceHeaders(response))

  // Check for HTTP errors (including 500)
  if (!response.ok) {
    // Surface HTTPException(detail=...) bodies — FastAPI returns
    // {detail: "string"} or {detail: [...]}, so the structured
    // ResponseSchema {error: {message}} branch is not the only shape
    // a 4xx body can take. Without this fall-through, callers see a
    // generic "HTTP error 400" instead of the backend's actual message.
    // Return error response (don't throw for apiFetcher)
    return data
  }

  return data
}

/**
 * Fetcher that throws on error - for use with SWR.
 * SWR expects the fetcher to throw on error for proper error handling.
 */
export async function swrFetcher<T>(
  url: string,
  options: FetcherOptions,
  getToken?: GetAuthToken,
): Promise<T> {
  const { method = 'GET', body, query, headers = {}, signal } = options

  const finalUrl = buildUrl(url, query)
  const token = await getBackendAuthToken(getToken)
  const trace = createTraceHeaders()

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...trace.headers,
    ...headers,
  }

  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`
  }

  const requestInit: RequestInit = {
    method,
    headers: requestHeaders,
  }

  if (body && method !== 'GET') {
    requestInit.body = JSON.stringify(body)
  }
  if (method === 'GET') {
    // SWR dedupes a key onto its pending request, so a read that never
    // settles pins every later mount of that key to a spinner until reload.
    // The signal also bounds the body read. Writes keep no deadline: an
    // aborted write may still have been applied server-side.
    requestInit.signal = AbortSignal.timeout(SWR_READ_TIMEOUT_MS)
  } else if (signal) {
    requestInit.signal = signal
  }

  const response = await fetch(finalUrl, requestInit)
  let data: unknown

  // Handle empty responses (e.g. 204 No Content) before parsing JSON
  if (
    response.status === 204 ||
    (response.ok && response.headers.get('content-length') === '0')
  ) {
    return null as T
  }

  const responseTrace = readTraceHeaders(response)

  try {
    data = await response.json()
  } catch (parseError) {
    // If JSON parsing fails, still check response status
    if (!response.ok) {
      const error = new ApiFetchError(
        `Failed to parse response: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`,
        response.status,
        finalUrl,
        undefined,
        undefined,
        responseTrace.traceId,
        responseTrace.requestId,
      )
      throw error
    }
    throw parseError
  }

  // Check for HTTP errors (including 500)
  if (!response.ok) {
    // Surface HTTPException(detail=...) bodies in addition to the
    // ResponseSchema {error: {message}} envelope. See `apiFetcher`
    // above for the rationale. FastAPI's http_exception_handler also
    // returns dict details verbatim as the TOP-LEVEL body (e.g.
    // {"code": "metrics_cache_unavailable", "message": "..."} with no
    // `error`/`detail` key), so fall back to top-level `code`/`message`
    // for that third shape — otherwise `ApiFetchError.code` stays
    // undefined and callers cannot branch on the backend error code.
    const body =
      typeof data === 'object' && data !== null
        ? (data as {
            error?: { message?: string; code?: string }
            detail?: unknown
            code?: unknown
            message?: unknown
          })
        : undefined
    const isTopLevelErrorBody =
      !!body && !('error' in body) && !('detail' in body)
    const topLevelMessage =
      isTopLevelErrorBody && typeof body.message === 'string'
        ? body.message
        : undefined
    const topLevelCode =
      isTopLevelErrorBody && typeof body.code === 'string'
        ? body.code
        : undefined
    const errorMessage =
      body?.error?.message ||
      apiErrorDetailText(data) ||
      topLevelMessage ||
      `HTTP error ${response.status}`
    const errorCode = body?.error?.code ?? topLevelCode
    const errorDetails =
      body?.error ?? body?.detail ?? (isTopLevelErrorBody ? body : undefined)

    const error = new ApiFetchError(
      errorMessage,
      response.status,
      finalUrl,
      errorCode,
      mergeTraceIntoDetails(errorDetails, responseTrace),
      responseTrace.traceId,
      responseTrace.requestId,
    )

    throw error
  }

  // Handle wrapped API responses (success/data/error format)
  if (
    typeof data === 'object' &&
    data !== null &&
    'success' in data &&
    'data' in data
  ) {
    if (!(data as { success: boolean }).success) {
      const errorMessage =
        (data as { error?: { message?: string } })?.error?.message ||
        'Request failed'
      const errorCode = (data as { error?: { code?: string } })?.error?.code
      const errorDetails = (data as { error?: unknown })?.error

      const error = new ApiFetchError(
        errorMessage,
        response.status,
        finalUrl,
        errorCode,
        mergeTraceIntoDetails(errorDetails, responseTrace),
        responseTrace.traceId,
        responseTrace.requestId,
      )

      throw error
    }
    return (data as { data: T }).data as T
  }

  return data as T
}

function attachTraceToApiResponse<T>(
  data: ApiResponse<T>,
  trace: TraceContext,
): ApiResponse<T> {
  if (!data.error) return data
  return {
    ...data,
    error: {
      ...data.error,
      details: mergeTraceIntoDetails(data.error.details, trace),
    },
  }
}
